// ============================================================================
// chatQueue.ts — очередь отправки сообщений (offline-first, ЕДИНЫЙ слой):
//   - отправка ВСЕГДА через sendChatMessage: онлайн → сервер напрямую
//     (ошибка сервера БРОСАЕТСЯ — без тихих фоллбэков); офлайн → durable
//     очередь (IndexedDB, привязана к базе, переживает перезагрузку);
//   - flushChatQueue — по событию восстановления связи (connection store),
//     НИКАКИХ таймеров-поллинга; clientRef сохраняется из очереди —
//     идемпотентность на сервере (повторная отправка вернёт существующее);
//   - сообщение из очереди при серверной ошибке ОСТАЁТСЯ (повторный флаш
//     при следующем восстановлении связи) — ничего не теряется.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { idbGet, idbSet } from '@/lib/storage/idb';
import { baseStoreGet } from '@/lib/storage/baseStore';
import { markConnectionOffline, useConnectionStore } from '@/lib/connection';
import { isTransportError } from '@/lib/errors';
import { create } from 'zustand';

/** Счётчики ожидающих отправки по чатам (индикатор «в очереди» в ChatThread) */
interface ChatQueueState {
  pendingByConv: Record<string, number>;
  setPending: (counts: Record<string, number>) => void;
}

export const useChatQueueStore = create<ChatQueueState>((set) => ({
  pendingByConv: {},
  setPending: (counts) => set({ pendingByConv: counts }),
}));

/** Пересчёт счётчиков очереди по чатам (после добавления/флаша) */
export async function refreshChatQueueCounters(): Promise<void> {
  const queue = await loadQueue();
  const counts: Record<string, number> = {};
  for (const m of queue) {
    counts[m.conversationId] = (counts[m.conversationId] || 0) + 1;
  }
  useChatQueueStore.getState().setPending(counts);
}

export interface QueuedChatMessage {
  conversationId: string;
  text: string;
  clientRef: string;
  attachments: string[];
  replyTo: string;
  queuedAt: number;
}

export interface SendChatResult {
  /** true — сообщение встало в офлайн-очередь (сервер недоступен) */
  queued: boolean;
}

async function queueKey(): Promise<string> {
  const base = await baseStoreGet?.();
  return `chat_queue:${base?.id || 'default'}`;
}

async function loadQueue(): Promise<QueuedChatMessage[]> {
  return (await idbGet<QueuedChatMessage[]>(await queueKey())) || [];
}

async function saveQueue(queue: QueuedChatMessage[]): Promise<void> {
  await idbSet(await queueKey(), queue);
}

/**
 * ЕДИНАЯ точка отправки сообщения (онлайн → сервер; офлайн → очередь).
 * clientRef генерируется ЗДЕСЬ (уникален на сессию) и сохраняется в очереди —
 * повторный флаш идемпотентен на сервере.
 */
export async function sendChatMessage(msg: Omit<QueuedChatMessage, 'clientRef' | 'queuedAt'>, myId: string): Promise<SendChatResult> {
  const status = useConnectionStore.getState().status;
  if (status === 'online' && navigator.onLine) {
    try {
      await getSDK().messenger.sendMessage({
        conversationId: msg.conversationId,
        text: msg.text,
        clientRef: `${myId}-${Date.now()}`,
        attachments: msg.attachments,
        replyTo: msg.replyTo,
      });
      return { queued: false };
    } catch (e) {
      if (!isTransportError(e)) throw e; // серверная ошибка — как раньше, БРОСАЕТСЯ
      markConnectionOffline(); // связь потеряна — режим офлайн
    }
  }
  // Офлайн: durable очередь (IndexedDB, переживает перезагрузку)
  const queue = await loadQueue();
  queue.push({
    ...msg,
    clientRef: `${myId}-${Date.now()}`,
    queuedAt: Date.now(),
  });
  await saveQueue(queue);
  await refreshChatQueueCounters();
  return { queued: true };
}

/** Счётчик ожидающих отправки (для индикации, если понадобится) */
export async function pendingChatMessages(): Promise<number> {
  return (await loadQueue()).length;
}

/**
 * Флаш очереди при восстановлении связи: по порядку, с сохранёнными
 * clientRef (идемпотентность); успех → удаление из очереди; серверная
 * ошибка → сообщение ОСТАЁТСЯ (повторный флаш при следующем online).
 */
export async function flushChatQueue(): Promise<void> {
  const status = useConnectionStore.getState().status;
  if (status !== 'online' || !navigator.onLine) return;
  const queue = await loadQueue();
  if (queue.length === 0) return;
  const failed: QueuedChatMessage[] = [];
  for (const m of queue) {
    try {
      await getSDK().messenger.sendMessage({
        conversationId: m.conversationId,
        text: m.text,
        clientRef: m.clientRef,
        attachments: m.attachments,
        replyTo: m.replyTo,
      });
    } catch (e) {
      if (isTransportError(e)) {
        // Связь потеряна — прекращаем флаш (остальные остаются в очереди
        // до следующего подтверждённого online), режим — офлайн.
        markConnectionOffline();
        failed.push(m, ...queue.slice(queue.indexOf(m) + 1));
        break;
      }
      failed.push(m); // серверная ошибка — сообщение остаётся (повтор позднее)
    }
  }
  await saveQueue(failed);
  await refreshChatQueueCounters();
}

let initialized = false;

/** Подписка на восстановление связи (событийная модель, без таймеров). main.tsx */
export function initChatQueue(): void {
  if (initialized) return;
  initialized = true;
  // Стартовый пересчёт счётчиков (очередь могла остаться с прошлой сессии)
  void refreshChatQueueCounters();
  useConnectionStore.subscribe((s, prev) => {
    if (s.status === 'online' && prev.status !== 'online') void flushChatQueue();
  });
}
